import React from 'react';
import NavigationHeader from '../../components/ui/NavigationHeader';

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const LegalPageLayout = ({ document }) => {
  const sections = document.sections.map((section, index) => ({
    ...section,
    id: `${index + 1}-${slugify(section.heading)}`,
  }));

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-[#08080a] dark:text-slate-100">
      <NavigationHeader />

      <main className="mx-auto grid w-full max-w-[1180px] flex-1 grid-cols-1 gap-8 px-4 pb-16 pt-20 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-12 lg:px-8">
        <aside className="lg:sticky lg:top-16 lg:h-[calc(100vh-5rem)] lg:self-start lg:overflow-y-auto lg:border-r lg:border-slate-200 lg:pr-5 dark:lg:border-slate-800">
          <div className="border-b border-slate-200 pb-5 dark:border-slate-800 lg:border-b-0">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
              {document.eyebrow}
            </p>
            <h1 className="text-xl font-semibold leading-7 tracking-normal text-slate-950 dark:text-white">
              {document.title}
            </h1>
            <p className="mt-2 text-[11px] leading-5 text-slate-500 dark:text-slate-400">
              Last updated: {document.lastUpdated}
            </p>
            <p className="mt-4 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {document.summary}
            </p>
          </div>

          <nav className="mt-5" aria-label={`${document.title} contents`}>
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              On This Page
            </p>
            <ol className="flex gap-2 overflow-x-auto pb-2 lg:block lg:space-y-1 lg:overflow-visible lg:pb-0">
              {sections.map((section) => (
                <li key={section.id} className="shrink-0 lg:shrink">
                  <a
                    href={`#${section.id}`}
                    className="block whitespace-nowrap border-l-0 border-slate-200 px-3 py-1.5 text-[11px] font-medium leading-4 text-slate-600 transition-colors hover:text-emerald-700 dark:border-slate-800 dark:text-slate-400 dark:hover:text-emerald-300 lg:whitespace-normal lg:border-l lg:px-3"
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <article className="min-w-0 max-w-3xl">
          {sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-20 border-b border-slate-200 py-7 first:pt-0 last:border-b-0 dark:border-slate-800"
            >
              <h2 className="mb-4 text-base font-semibold leading-6 tracking-normal text-slate-950 dark:text-white">
                {section.heading}
              </h2>

              {section.paragraphs?.map((paragraph, index) => (
                <p key={`p-${index}`} className="mb-3 text-[13px] leading-6 text-slate-700 dark:text-slate-300">
                  {paragraph}
                </p>
              ))}

              {section.bullets && (
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  {section.bullets.map((item, index) => (
                    <li key={`b-${index}`} className="text-[13px] leading-6 text-slate-700 dark:text-slate-300">
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {section.quote && (
                <blockquote className="my-4 border-l-2 border-emerald-500 bg-emerald-50/70 px-4 py-3 dark:bg-emerald-950/20">
                  <p className="m-0 text-[13px] font-medium leading-6 text-slate-900 dark:text-slate-100">
                    {section.quote}
                  </p>
                </blockquote>
              )}

              {section.closingParagraphs?.map((paragraph, index) => (
                <p key={`cp-${index}`} className="mb-3 text-[13px] leading-6 text-slate-700 dark:text-slate-300">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
};

export default LegalPageLayout;
